/**
 * Quick Add Dialogs for Cart Page
 * Collection of quick add dialogs for Veneer, Laminate, Hardware, and Adhesive
 *
 * Per-field PDF order markers
 * ---------------------------
 * Every field whose value composes the line shown on the PDF carries a
 * small circled-digit prefix (①, ②, ③, ④) telling the staff which
 * order the parts appear in. The line is built with `joinWithHyphen` so
 * an empty field doesn't introduce a leading "- " or empty segment.
 */
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Leaf, Palette, Sparkles, Wrench, Pipette, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { SizeInput } from './SizeInput';
import { sizeToSqft } from '../../lib/sizeToSqftStandalone';
import { 
  VENEER_SPECIES_SUGGESTIONS, 
  ADHESIVE_WEIGHTS, 
  HARDWARE_RATE_TYPES, 
  LAMINATE_SIZES,
  VENEER_SIZES,
  VENEER_UNITS,
  LAMINATE_UOM_OPTIONS
} from './constants';

// Small reusable "label with PDF-order marker" used by every Quick Add
// field that composes the customer-PDF line. Keeps the dialogs visually
// consistent so the staff learns one pattern.
//
//   <PdfOrderLabel order={1}>Description</PdfOrderLabel>
//   →  "① Description  (shows on PDF)"
const ORDER_GLYPHS = ['①', '②', '③', '④', '⑤'];
function PdfOrderLabel({ order, children, optional = true, className = '' }) {
  const glyph = ORDER_GLYPHS[(order || 1) - 1] || '•';
  return (
    <Label className={`text-xs text-[#5C6150] flex items-center gap-1 ${className}`}>
      <span className="font-semibold text-[#556B2F]">{glyph}</span>
      <span>{children}</span>
      <span className="text-[10px] text-[#8F9485]">{optional ? '(shows on PDF, optional)' : '(shows on PDF)'}</span>
    </Label>
  );
}

/**
 * Small inline toggle for "Show brand on PDF". When unchecked the cart item
 * is added with show_brand:false so the brand name is hidden from the
 * customer PDF (useful for white-label / generic items).
 */
function BrandVisibilityToggle({ checked, onChange, testId = 'brand-visible-toggle' }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border transition-colors ${
        checked
          ? 'border-[#556B2F]/40 bg-[#556B2F]/10 text-[#556B2F]'
          : 'border-[#8F9485]/30 bg-[#F2F4EF] text-[#5C6150]'
      }`}
      data-testid={testId}
      title={checked ? 'Brand will be visible on customer PDF' : 'Brand hidden from customer PDF'}
    >
      {checked ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
      <span>Brand on PDF: <strong>{checked ? 'Visible' : 'Hidden'}</strong></span>
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────
// QuickAddMathPreview — generic preview banner used by every Quick Add
// dialog. Reads the dialog state live and renders the exact formula +
// total the cart will store. Eliminates the "is qty in sheets or sq ft?"
// ambiguity that caused iter 105's production bug.
//
// Mirrors `addQuickVeneer` in lib/store.js + the laminate/hardware
// handlers in pages/CartPage.js. Keep these in sync.
// ────────────────────────────────────────────────────────────────────
function QuickAddMathPreview({ data, category = 'Veneers', testId = 'qa-math-preview' }) {
  const rate = parseFloat(data?.rate) || 0;
  const qty = parseFloat(data?.quantity) || 0;
  if (rate <= 0 || qty <= 0) {
    return (
      <p className="text-[11px] text-[#8F9485] text-center italic">
        Enter rate &amp; quantity to see the calculated amount.
      </p>
    );
  }
  const uom = (data.uom || 'sqft').toLowerCase();
  const unitUpper = String(data.unit || '').toUpperCase();
  const isSqftUnit = unitUpper === 'SQ FT' || unitUpper === 'SQFT' || unitUpper === 'SQ.FT';
  const sqftPerSheet = data.size ? sizeToSqft(data.size, category) : 0;
  // Hardware uses `rate_type` (per piece, per kg, per running feet, per set,
  // etc.) rather than uom; treat it as a simple `rate × qty` line and label
  // the unit from rate_type.
  const rateType = (data.rate_type || '').toLowerCase();
  const isHardwareCat = category === 'Hardware';

  let formula, total;
  if (isHardwareCat) {
    const unitLbl = rateType ? rateType.replace('per ', '') : 'unit';
    formula = `₹${rate}/${unitLbl} × ${qty} = `;
    total = rate * qty;
  } else if (uom === 'piece' || uom === 'sheet') {
    const unitLbl = uom === 'piece' ? 'piece' : 'sheet';
    formula = `₹${rate}/${unitLbl} × ${qty} = `;
    total = rate * qty;
  } else if (uom === 'sqft' && !isSqftUnit && sqftPerSheet > 0) {
    const unitWord = unitUpper.toLowerCase() || 'sheet';
    formula = `₹${rate}/sqft × ${sqftPerSheet} sqft/${unitWord} × ${qty} = `;
    total = rate * sqftPerSheet * qty;
  } else if (uom === 'sqft' && isSqftUnit) {
    formula = `₹${rate}/sqft × ${qty} sqft = `;
    total = rate * qty;
  } else {
    formula = `₹${rate} × ${qty} = `;
    total = rate * qty;
  }
  const totalStr = total.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return (
    <div className="px-3 py-2 bg-[#FDF6E1]/50 border border-[#DAA520]/30 rounded text-[11px] text-[#8B6914] text-center" data-testid={testId}>
      <span className="font-mono">{formula}</span>
      <span className="font-bold ml-0.5">₹{totalStr}</span>
    </div>
  );
}

// Back-compat alias — VeneerMathPreview is the original name still used
// inside the Veneer dialog. Delegates to the generic component.
function VeneerMathPreview({ data }) {
  return <QuickAddMathPreview data={data} category="Veneers" testId="veneer-math-preview" />;
}

// ────────────────────────────────────────────────────────────────────
// PdfLinePreview — dashed "WILL PRINT ON PDF AS:" box shown in EVERY
// Quick Add dialog so staff see the exact composed line before adding.
// `hiddenBrand` renders the small "(Brand X hidden from customer …)"
// hint when the brand toggle is off but a brand was typed.
// ────────────────────────────────────────────────────────────────────
function PdfLinePreview({ line, hiddenBrand, sizeNote, testId = 'qa-pdf-preview' }) {
  return (
    <div className="bg-[#FAF8F1] border border-dashed border-[#556B2F]/40 rounded p-2.5" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wide text-[#556B2F] font-semibold mb-0.5">
        Will print on PDF as:
      </p>
      <p className="font-mono text-sm text-[#1f2a16] break-words">{line || '— fill the fields to preview —'}</p>
      {sizeNote && (
        <p className="text-[10px] text-[#8F9485] mt-1">Size column: <strong>{sizeNote}</strong></p>
      )}
      {hiddenBrand && (
        <p className="text-[10px] text-[#8F9485] mt-1">
          (Brand <strong>{hiddenBrand}</strong> hidden from customer; still shows on internal PDF.)
        </p>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// SaveRememberRow — the green "Save & Remember" checkbox used when a
// Quick Add dialog is opened from the PRODUCTS page. Cart-page dialogs
// don't pass `saveRemember`, so cart quick adds stay quotation-only.
// ────────────────────────────────────────────────────────────────────
function SaveRememberRow({ checked, onChange, id, testId = 'save-remember-checkbox' }) {
  return (
    <div className="flex items-center gap-2 p-3 bg-[#E8F5E9] rounded-lg border border-[#4CAF50]/30">
      <input
        type="checkbox"
        id={id}
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-[#4CAF50]"
        data-testid={testId}
      />
      <label htmlFor={id} className="text-sm text-[#2E7D32] cursor-pointer">
        <strong>Save &amp; Remember</strong> — saved to your catalog; next time this will auto-fill
      </label>
    </div>
  );
}




/**
 * Quick Add Veneer Dialog
 */
export function QuickAddVeneerDialog({ 
  open, 
  onOpenChange, 
  data, 
  setData, 
  onAdd,
  saveRemember,
}) {
  const handleAdd = () => {
    if (!data.species) {
      toast.error('Species is required');
      return;
    }
    if (!data.rate || parseFloat(data.rate) <= 0) {
      toast.error('Rate is required');
      return;
    }
    onAdd();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="quick-veneer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Leaf className="w-5 h-5 text-[#8B4513]" />
            Quick Add Natural Veneer
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label className="text-sm font-medium">Species * <span className="text-xs text-muted-foreground">(Type or select)</span></Label>
            <Input
              list="species-suggestions"
              value={data.species}
              onChange={(e) => setData(prev => ({ ...prev, species: e.target.value }))}
              placeholder="e.g., Walnut, Teak, Oak..."
              className="mt-1"
              data-testid="veneer-species-input"
            />
            <datalist id="species-suggestions">
              {VENEER_SPECIES_SUGGESTIONS.map(s => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div>
            <Label className="text-sm font-medium">Product Code</Label>
            <Input
              value={data.product_code}
              onChange={(e) => setData(prev => ({ ...prev, product_code: e.target.value }))}
              placeholder="Your code (optional)"
              className="mt-1"
              data-testid="veneer-code-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <SizeInput
                value={data.size}
                onChange={(v) => setData(prev => ({ ...prev, size: v }))}
                helperText="e.g. 8 × 4 (feet) or 84 × 36 (inches). Leave blank if N/A."
                testIdPrefix="veneer-size"
                defaultUnit="ft"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Rate *</Label>
              <Input
                type="number"
                value={data.rate}
                onChange={(e) => setData(prev => ({ ...prev, rate: e.target.value }))}
                placeholder="Rs."
                className="mt-1"
                data-testid="veneer-rate-input"
              />
            </div>
          </div>
          {/* Rate UOM — Veneers default to Per Sq Ft (rate × sheet area × qty).
              Switch to Per Sheet for flat-priced veneers, or Per Piece for
              unusual ad-hoc items. The label "Quantity" below ALWAYS means
              number of sheets/pieces — never loose square feet. */}
          {/* Auto-sync uom ↔ unit so the math model and the qty unit are
              always consistent (iter 105 follow-up). Picking unit=SQ FT
              keeps uom=sqft but the math now treats qty as loose sqft
              (no sheet expansion). Picking uom=piece auto-flips unit→NOS;
              uom=sheet auto-flips unit→SHEET. */}
          <div>
            <Label className="text-sm font-medium">Rate Per</Label>
            <Select
              value={data.uom || 'sqft'}
              onValueChange={(v) => setData(prev => {
                let nextUnit = prev.unit;
                if (v === 'piece') nextUnit = 'NOS';
                else if (v === 'sheet') nextUnit = 'SHEET';
                else if (v === 'sqft' && prev.unit !== 'SQ FT' && prev.unit !== 'SHEET') nextUnit = 'SHEET';
                return { ...prev, uom: v, unit: nextUnit };
              })}
            >
              <SelectTrigger className="mt-1" data-testid="veneer-uom-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sqft">Per Sq Ft (rate × sheet area × qty)</SelectItem>
                <SelectItem value="sheet">Per Sheet (rate × qty)</SelectItem>
                <SelectItem value="piece">Per Piece (rate × qty)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">Quantity</Label>
              <Input
                type="number"
                min="1"
                value={data.quantity}
                onChange={(e) => setData(prev => ({ ...prev, quantity: e.target.value }))}
                onFocus={(e) => e.target.select()}
                className="mt-1"
                data-testid="veneer-qty-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Unit</Label>
              <Select value={data.unit} onValueChange={(v) => setData(prev => {
                // When user picks SQ FT for quantity, force uom=sqft (so
                // rate stays per-sqft) — the addQuickVeneer logic will then
                // bill rate × qty (no sheet expansion). Picking SHEET/NOS
                // leaves uom alone.
                const next = { ...prev, unit: v };
                if (v === 'SQ FT' && prev.uom === 'piece') next.uom = 'sqft';
                return next;
              })}>
                <SelectTrigger className="mt-1" data-testid="veneer-unit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VENEER_UNITS.map(u => (
                    <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Inline preview — shows the exact math the cart will use so the
              salesperson sees the result BEFORE hitting Add. Removes any
              ambiguity between "per sq ft × sheets" vs "per sq ft × loose
              sq ft" because both choices now render different totals. */}
          {/* Live PDF preview — mirrors formatPdfDescription's Veneers
              branch: "CODE - SPECIES" (code optional). */}
          <PdfLinePreview
            line={[data.product_code, data.species].map(p => (p || '').trim()).filter(Boolean).join(' - ')}
            sizeNote={(data.size || '').trim() || null}
            testId="veneer-pdf-preview"
          />
          <VeneerMathPreview data={data} />
          <div className="flex items-center justify-between gap-2 pt-1">
            <BrandVisibilityToggle
              checked={data.show_brand !== false}
              onChange={(v) => setData(prev => ({ ...prev, show_brand: v }))}
              testId="veneer-brand-visible-toggle"
            />
          </div>
          {saveRemember && (
            <SaveRememberRow
              id="saveVeneerToDb"
              checked={saveRemember.checked}
              onChange={saveRemember.onChange}
              testId="veneer-save-remember-checkbox"
            />
          )}
          <div className="flex gap-2 mt-6">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleAdd} className="flex-1 bg-[#8B4513] hover:bg-[#6B3410] text-white" data-testid="add-veneer-btn">
              <Plus className="w-4 h-4 mr-1" /> Add to Quotation
            </Button>
          </div>
          {!saveRemember && (
            <p className="text-xs text-[#8F9485] text-center">
              Note: Quick add veneers are only for this quotation and won&apos;t be saved to your product database.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Quick Add Laminate/Highlighter Dialog
 */
export function QuickAddLaminateDialog({ 
  open, 
  onOpenChange, 
  data, 
  setData, 
  onAdd,
  saveRemember,
}) {
  const isHighlighter = data.category === 'Highlighters';

  const handleAdd = () => {
    if (!data.product_code || !data.rate) {
      toast.error('Code and Rate are required');
      return;
    }
    onAdd();
  };

  // Live preview of how the line will print on the PDF. We mirror the
  // server-side rule exactly: drop empty parts, join with " - ". When
  // the brand toggle is ON we ALSO prefix the brand (matches the
  // internal-PDF path); when OFF we show only the description-code-
  // thickness composition the customer will see.
  const previewParts = [
    data.show_brand !== false ? data.brand : '',
    data.description,
    data.product_code,
    data.thickness,
  ].map(p => (p || '').trim()).filter(Boolean);
  const livePreview = previewParts.length ? previewParts.join(' - ') : '— fill code to preview —';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="quick-laminate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isHighlighter ? (
              <><Sparkles className="w-5 h-5 text-[#9C27B0]" /> Quick Add Highlighter</>
            ) : (
              <><Palette className="w-5 h-5 text-[#1976D2]" /> Quick Add Laminate</>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Brand stays at the top — it's the canonical sort key and only
              shows on the customer PDF if the brand-visibility toggle is on
              (default OFF). Internal PDF always shows the brand. */}
          <div>
            <Label className="text-xs text-[#5C6150] flex items-center gap-1">
              Brand
              <span className="text-[10px] text-[#8F9485]">(internal PDF always shows; customer PDF only if toggled below)</span>
            </Label>
            <Input
              value={data.brand}
              onChange={(e) => setData(d => ({ ...d, brand: e.target.value.toUpperCase() }))}
              placeholder="e.g., GREENLAM"
              className="mt-1 uppercase text-sm"
              data-testid="lam-brand-input"
            />
          </div>
          {/* ① Description — first part of the composed PDF line. Optional. */}
          <div>
            <PdfOrderLabel order={1}>Description</PdfOrderLabel>
            <Input
              value={data.description}
              onChange={(e) => setData(d => ({ ...d, description: e.target.value }))}
              placeholder="e.g., Matte, Walnut Burl, Backlit…"
              className="mt-1 text-sm"
              data-testid="lam-description-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <PdfOrderLabel order={2} optional={false}>Code *</PdfOrderLabel>
              <Input
                value={data.product_code}
                onChange={(e) => setData(d => ({ ...d, product_code: e.target.value.toUpperCase() }))}
                placeholder="e.g., GL-1010"
                className="mt-1 uppercase font-mono text-sm"
                data-testid="lam-code-input"
              />
            </div>
            <div>
              <PdfOrderLabel order={3}>Thickness</PdfOrderLabel>
              <Input
                value={data.thickness}
                onChange={(e) => setData(d => ({ ...d, thickness: e.target.value.toUpperCase() }))}
                placeholder="e.g., 1MM, 2MM"
                className="mt-1 uppercase text-sm"
                data-testid="lam-thickness-input"
              />
            </div>
          </div>
          <div>
            <SizeInput
              value={data.size}
              onChange={(v) => setData(d => ({ ...d, size: v }))}
              testIdPrefix="lam-size"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-[#5C6150]">Rate *</Label>
              <Input
                type="text" 
                inputMode="numeric"
                value={data.rate}
                onChange={(e) => setData(d => ({ ...d, rate: e.target.value.replace(/[^0-9.]/g, '') }))}
                placeholder="₹"
                className="mt-1 font-mono text-sm"
                data-testid="lam-rate-input"
              />
            </div>
            <div>
              <Label className="text-xs text-[#5C6150]">UOM</Label>
              <Select value={data.uom || 'sheet'} onValueChange={(v) => setData(d => ({ ...d, uom: v }))}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid="lam-uom-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LAMINATE_UOM_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#5C6150]">Qty</Label>
              <Input
                type="number" 
                min="1"
                value={data.quantity ?? ''}
                onChange={(e) => setData(d => ({ ...d, quantity: e.target.value }))}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => {
                  const n = parseInt(e.target.value, 10);
                  setData(d => ({ ...d, quantity: (Number.isFinite(n) && n > 0) ? n : 1 }));
                }}
                className="mt-1 text-sm"
                data-testid="lam-qty-input"
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 pt-1">
            <BrandVisibilityToggle
              checked={data.show_brand !== false}
              onChange={(v) => setData(d => ({ ...d, show_brand: v }))}
              testId="lam-brand-visible-toggle"
            />
          </div>
          {/* Live PDF preview — staff sees exactly what will print as
              they type. Stale composes are kept out of the customer-PDF
              preview by honouring the brand-visible toggle. */}
          <div className="bg-[#FAF8F1] border border-dashed border-[#556B2F]/40 rounded p-2.5 mt-2" data-testid="lam-pdf-preview">
            <p className="text-[10px] uppercase tracking-wide text-[#556B2F] font-semibold mb-0.5">
              Will print on PDF as:
            </p>
            <p className="font-mono text-sm text-[#1f2a16] break-words">{livePreview}</p>
            {data.show_brand === false && data.brand && (
              <p className="text-[10px] text-[#8F9485] mt-1">
                (Brand <strong>{data.brand}</strong> hidden from customer; still shows on internal PDF.)
              </p>
            )}
          </div>
          {/* Live math preview — same component the Veneer dialog uses.
              Renders the exact formula + total the cart will store. */}
          <QuickAddMathPreview data={data} category={isHighlighter ? 'Highlighters' : 'Laminates'} testId="lam-math-preview" />
          {saveRemember && (
            <SaveRememberRow
              id="saveLamToDb"
              checked={saveRemember.checked}
              onChange={saveRemember.onChange}
              testId="lam-save-remember-checkbox"
            />
          )}
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 h-9 text-sm">Cancel</Button>
            <Button onClick={handleAdd} className="flex-1 bg-[#1976D2] hover:bg-[#1565C0] text-white h-9 text-sm" data-testid="add-lam-btn">
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Quick Add Hardware Dialog
 */
export function QuickAddHardwareDialog({ 
  open, 
  onOpenChange, 
  data, 
  setData, 
  onAdd,
  saveRemember,
}) {
  const handleAdd = () => {
    if (!data.brand || !data.product_name || !data.rate) {
      toast.error('Brand, Product Name and Rate are required');
      return;
    }
    onAdd();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="quick-hardware-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-[#4A5568]" /> Quick Add Hardware
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#5C6150]">Brand *</Label>
              <Input
                value={data.brand}
                onChange={(e) => setData(d => ({ ...d, brand: e.target.value.toUpperCase() }))}
                placeholder="e.g., HETTICH"
                className="mt-1 uppercase text-sm"
              />
            </div>
            <div>
              <Label className="text-xs text-[#5C6150]">Name *</Label>
              <Input
                value={data.product_name}
                onChange={(e) => setData(d => ({ ...d, product_name: e.target.value }))}
                placeholder="e.g., Tandem"
                className="mt-1 text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#5C6150]">Specs</Label>
              <Input
                value={data.specs}
                onChange={(e) => setData(d => ({ ...d, specs: e.target.value }))}
                placeholder="e.g., 350mm"
                className="mt-1 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs text-[#5C6150]">Rate Type</Label>
              <Select value={data.rate_type} onValueChange={(v) => setData(d => ({ ...d, rate_type: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HARDWARE_RATE_TYPES.map(rt => (
                    <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <SizeInput
            value={data.size}
            onChange={(v) => setData(d => ({ ...d, size: v }))}
            helperText="Optional. Type feet / mm / inches — anything. Leave blank if N/A."
            testIdPrefix="hw-size"
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#5C6150]">Rate *</Label>
              <Input
                type="text" 
                inputMode="numeric"
                value={data.rate}
                onChange={(e) => setData(d => ({ ...d, rate: e.target.value.replace(/[^0-9.]/g, '') }))}
                placeholder="₹"
                className="mt-1 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-xs text-[#5C6150]">Qty</Label>
              <Input
                type="number" 
                min="1"
                value={data.quantity ?? ''}
                onChange={(e) => setData(d => ({ ...d, quantity: e.target.value }))}
                onFocus={(e) => e.target.select()}
                onBlur={(e) => {
                  const n = parseInt(e.target.value, 10);
                  setData(d => ({ ...d, quantity: (Number.isFinite(n) && n > 0) ? n : 1 }));
                }}
                className="mt-1 text-sm"
                data-testid="hw-qty-input"
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 pt-1">
            <BrandVisibilityToggle
              checked={data.show_brand !== false}
              onChange={(v) => setData(d => ({ ...d, show_brand: v }))}
              testId="hw-brand-visible-toggle"
            />
          </div>
          {/* Live PDF preview — mirrors the handler's composed line:
              brand (if toggled) + name + specs joined with hyphens. */}
          <PdfLinePreview
            line={[
              data.show_brand !== false ? data.brand : '',
              data.product_name,
              data.specs,
            ].map(p => (p || '').trim()).filter(Boolean).join(' - ')}
            hiddenBrand={data.show_brand === false && data.brand ? data.brand : null}
            sizeNote={((data.size || '').trim() || (data.specs || '').trim()) || null}
            testId="hw-pdf-preview"
          />
          <QuickAddMathPreview data={data} category="Hardware" testId="hw-math-preview" />
          {saveRemember && (
            <SaveRememberRow
              id="saveHwToDb"
              checked={saveRemember.checked}
              onChange={saveRemember.onChange}
              testId="hw-save-remember-checkbox"
            />
          )}
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 h-9 text-sm">Cancel</Button>
            <Button onClick={handleAdd} className="flex-1 bg-[#4A5568] hover:bg-[#2D3748] text-white h-9 text-sm">
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Quick Add Adhesive Dialog
 */
export function QuickAddAdhesiveDialog({ 
  open, 
  onOpenChange, 
  data, 
  setData, 
  onAdd,
  saveRemember,
}) {
  const handleAdd = () => {
    if (!data.brand || !data.product_name || !data.rate) {
      toast.error('Brand, Product Name and Rate are required');
      return;
    }
    onAdd();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="quick-adhesive-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pipette className="w-5 h-5 text-[#9B4DCA]" /> Quick Add Adhesive
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#5C6150]">Brand *</Label>
              <Input
                value={data.brand}
                onChange={(e) => setData(d => ({ ...d, brand: e.target.value.toUpperCase() }))}
                placeholder="e.g., FEVICOL"
                className="mt-1 uppercase text-sm"
              />
            </div>
            <div>
              <Label className="text-xs text-[#5C6150]">Name *</Label>
              <Input
                value={data.product_name}
                onChange={(e) => setData(d => ({ ...d, product_name: e.target.value }))}
                placeholder="e.g., Marine"
                className="mt-1 text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#5C6150]">Weight</Label>
              <Select value={data.weight} onValueChange={(v) => setData(d => ({ ...d, weight: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ADHESIVE_WEIGHTS.map(w => (
                    <SelectItem key={w} value={w}>{w.replace('KG', ' KG').replace('GMS', ' GMS')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#5C6150]">Rate *</Label>
              <Input
                type="text" 
                inputMode="numeric"
                value={data.rate}
                onChange={(e) => setData(d => ({ ...d, rate: e.target.value.replace(/[^0-9.]/g, '') }))}
                placeholder="₹"
                className="mt-1 font-mono text-sm"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-[#5C6150]">Qty</Label>
            <Input
              type="number" 
              min="1"
              value={data.quantity ?? ''}
              onChange={(e) => setData(d => ({ ...d, quantity: e.target.value }))}
              onFocus={(e) => e.target.select()}
              onBlur={(e) => {
                const n = parseInt(e.target.value, 10);
                setData(d => ({ ...d, quantity: (Number.isFinite(n) && n > 0) ? n : 1 }));
              }}
              className="mt-1 w-20 text-sm"
              data-testid="adh-qty-input"
            />
          </div>
          <SizeInput
            value={data.size}
            onChange={(v) => setData(d => ({ ...d, size: v }))}
            helperText="Optional. Leave blank to use weight as the size column."
            testIdPrefix="adh-size"
          />
          <div className="flex items-center justify-between gap-2 pt-1">
            <BrandVisibilityToggle
              checked={data.show_brand !== false}
              onChange={(v) => setData(d => ({ ...d, show_brand: v }))}
              testId="adh-brand-visible-toggle"
            />
          </div>
          {/* Live PDF preview — brand (if toggled) + name; weight/size
              shows in the Size column. */}
          <PdfLinePreview
            line={[
              data.show_brand !== false ? data.brand : '',
              data.product_name,
            ].map(p => (p || '').trim()).filter(Boolean).join(' - ')}
            hiddenBrand={data.show_brand === false && data.brand ? data.brand : null}
            sizeNote={((data.size || '').trim() || data.weight) || null}
            testId="adh-pdf-preview"
          />
          <QuickAddMathPreview data={data} category="Adhesives" testId="adh-math-preview" />
          {saveRemember && (
            <SaveRememberRow
              id="saveAdhToDb"
              checked={saveRemember.checked}
              onChange={saveRemember.onChange}
              testId="adh-save-remember-checkbox"
            />
          )}
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 h-9 text-sm">Cancel</Button>
            <Button onClick={handleAdd} className="flex-1 bg-[#9B4DCA] hover:bg-[#7B3DA0] text-white h-9 text-sm">
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default {
  QuickAddVeneerDialog,
  QuickAddLaminateDialog,
  QuickAddHardwareDialog,
  QuickAddAdhesiveDialog
};

